import { CanadaProvincesOptions, UsStatesOptions } from '@nuso/forms/util';
import type { TnCreatePortOrderAddressInput } from './tn-porting';

// Port order field rules shared by the create/update resolvers and the split file
// (POR-206). Each returns a user-facing message for the first violation, or null.

const present = (v?: string | null): boolean => !!v?.trim();

// Enforces the residential/business subscriber-name rule that GraphQL can't
// express (the name fields are all nullable). A value counts as present only
// when it has non-whitespace content, matching the trimming in
// resolveSalesforceCaseId.
export function validateSubscriberNameFields(input: {
  isResidential: boolean;
  businessName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): string | null {
  if (input.isResidential) {
    if (!present(input.firstName) || !present(input.lastName)) {
      return 'First and last name are required for a residential port order.';
    }
    if (present(input.businessName)) {
      return 'Business name must not be set for a residential port order.';
    }
  } else {
    if (!present(input.businessName)) {
      return 'Business name is required for a business port order.';
    }
    if (present(input.firstName) || present(input.middleName) || present(input.lastName)) {
      return 'First, middle, and last name must not be set for a business port order.';
    }
  }
  return null;
}

const STATES_BY_COUNTRY: Record<string, ReadonlySet<string>> = {
  US: new Set(UsStatesOptions.map(o => o.value)),
  CA: new Set(CanadaProvincesOptions.map(o => o.value)),
};

const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;

type PortOrderAddressRuleInput = Partial<Record<keyof TnCreatePortOrderAddressInput, string | null>>;

const REQUIRED_ADDRESS_FIELDS: Array<[keyof TnCreatePortOrderAddressInput, string]> = [
  ['number', 'Number'],
  ['streetName', 'Street name'],
  ['city', 'City'],
  ['stateProvince', 'State'],
  ['zipCode', 'Zip'],
  ['country', 'Country'],
];

// The service-address rules the port order forms apply client-side: the six required
// fields, country US or CA, a state or province of that country, and a US zip of
// 5 digits with an optional dash and 4 digits.
export function validatePortOrderAddress(address: PortOrderAddressRuleInput): string | null {
  const missing = REQUIRED_ADDRESS_FIELDS.filter(([key]) => !present(address[key])).map(([, label]) => label);
  if (missing.length > 0) {
    return `${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} required.`;
  }
  const country = (address.country as string).trim();
  const states = STATES_BY_COUNTRY[country];
  if (!states) {
    return 'Country must be US or CA.';
  }
  if (!states.has((address.stateProvince as string).trim())) {
    return country === 'US'
      ? 'State must be a two-letter US state code.'
      : 'State must be a two-letter Canadian province code.';
  }
  if (country === 'US' && !US_ZIP_REGEX.test((address.zipCode as string).trim())) {
    return 'Zip must be 5 digits with an optional dash and 4 digits.';
  }
  return null;
}
